import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { useStore } from "../../../../store/store";
import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import {BasketContent, BasketWrapper, BasketHeader, BasketHeaderIcon, BasketHeadline, BasketProgress, BasketProgressText, BasketProgressbar, BasketProgressFill, BasketMembershipWrapper, BasketMembershipRow, BasketCheckmark, BasketProduct, BasketProductPrice, BasketTotalWrapper, BasketTotalRow, BasketTotal, BasketTotalPrice, BasketUnionOffer, BasketValueList, BasketValueItem, BasketTotalText, BasketPackageInfoContainer, BasketPackageInfoHeadline, BasketPackageInfoUl, BasketPackageInfoLi, BasketPackageInfoHeader, BasketPackageInfoIcon, BasketPackageRow, Contact, InfoText} from "./Basket.style"
import { formatNumber } from "../../../../utils/formatNumber";
import HelpForm from "../HelpForm";
import Analytics from "../../../../utils/analytics";

interface Props {
    marginTop: boolean,
    openHelpForm: () => void;
}

export default function Basket({marginTop, openHelpForm} : Props) {
    const accordionRoute = useStore.useAccordionRoute();
    const completedAccordionRoute = useStore.useAccordionRouteCompletedArray();
    const payload = useStore.usePayload();
    const translations = useStore.useTranslations();
    const [progressPercent, setProgressPercent] = useState(0)
    const [chosenAdvantages, setChosenAdvantages] = useState([""]);
    const offerEnabled = useStore.useOfferEnabled();
    const packagePrice = Number(translations?.productsStep.salaryInsuranceAndUnion.singlePrice);
    const [selfemployedProducts, setSelfemployedProducts] = useState([""])
    const [showSelfemployedProductInfo, setShowSelfemployedProductsInfo] = useState(false);
    const [numberStep, setNumberStep] = useState<number>(1);
    const goBackNav = useStore.useGoBackNav();
    const IsDesktop = useStore.useIsDesktop();
    const [salaryInsurancePrice, setSalaryInsurancePrice] = useState<number>();

    //Calcuclate progress for progressbar
    useEffect(() => {
      let step = 0
      if (accordionRoute === AccordionRoutesEnum.OccupationStep) {
        step = 1
      } else {
        if(goBackNav > 0) {
            step = completedAccordionRoute.length + 1 - goBackNav
        } else {
            step = completedAccordionRoute.length + 1
        }
      }
      let stepPercent = (step / 6) * 100
      setProgressPercent(stepPercent)
      setNumberStep(step)
    }, [accordionRoute, completedAccordionRoute, goBackNav])
    
    
    //Get price of unemplomentbenefits
    const unemploymentBenefitsPrice = () => {
        if (payload.occupation === "SelfEmployed") {
          return `${translations?.productsStep.unemploymentInsurance.price_selfemployed}`;
        } else if (payload.occupation === "Student") {
          return `0`;
        } else {
          return `${translations?.productsStep.unemploymentInsurance.price}`;
        }
    }
    
    //Price of unemploymentBenefits pr. month based on occupation (if the product is not choosen or occupation is student it returns 0)
    const unemploymentBenefitPrice = (payload.wantsUnemploymentBenefits && payload.occupation !== "Student" && payload.occupation !== "SelfEmployed") ? Number(translations?.summaryStep.unemploymentInsurance.price) : payload.wantsUnemploymentBenefits && payload.occupation === "SelfEmployed" ? Number(translations?.productsStep.unemploymentInsurance.price_selfemployed) : 0;

    //Price of union pr.month (if the product is not choosen it returns 0)
    let unionPrice: number;
    if(payload.wantsUnion) {
        if(offerEnabled && payload.wantsUnemploymentBenefits && payload.occupation !== "Student") {
            unionPrice = 0;
        } else if ((offerEnabled && !payload.wantsUnemploymentBenefits) || !offerEnabled || (offerEnabled && payload.occupation === "Student")) {
            unionPrice = Number(translations?.summaryStep.union.price);
        }
    } else {
        unionPrice = 0
    }

    //Full price with offer 
    const fullPrice = useMemo(() => {
        //If the user has come from the salaryConfigCard
        if(payload.wantsUnemploymentBenefits && payload.wantsSalaryInsurance && salaryInsurancePrice !== undefined) {
            return Number(translations?.summaryStep.unemploymentInsurance.price) + salaryInsurancePrice
        } else {
            let totalAmountPrice = 0
            if(payload.wantsSalaryInsuranceAndUnion) {
                //totalAmountPrice = Number(translations?.productsStep.salaryInsuranceAndUnion.price)
                totalAmountPrice = 560
            } else {
    
                totalAmountPrice = unemploymentBenefitPrice + unionPrice;
            }
            return totalAmountPrice;
        }
    }, [payload, translations]);

    //Full price for union and unemploymentbenefits without offer
    const basePrice = useMemo(() => {
        if(translations) {
            return Number(translations.productsStep.unemploymentInsurance.price) + Number(translations.productsStep.union.price)
        } else {
            return 0
        }
    }, [translations])

    //Advantages for anyone that is not selfemployed
    const advantages = [
        "Du har en solid økonomisk grundsikring",
        "Du har professionel opbakning før, under og efter ansættelsen",
        "Dine vilkår bliver taget alvorligt",
        "Din indkomst er bedre beskyttet",
        "Du reducerer økonomisk usikkerhed",
        "Økonomisk tryghed ved ledighed",
        "Juridisk hjælp på jobbet",
        "Økonomisk tryghed ved ledighed",
        "Juridisk hjælp på jobbet",
        "Din dækning matcher din økonomi",
        "Du har komplet og sammenhængende dækning",
        "Du har styr på økonomien ved jobtab",
        "Du minimerer indkomstnedgang",
        "Du står stærkt både ved konflikt og jobtab",
        "Du kombinerer rådgivning og økonomisk sikkerhed",
        "Du er økonomisk sikret, hvis virksomheden stopper",
        "Op til 6.000 kr. oveni dagpengene"
    ]

    const advantagesSelfemployed = [
        "Du er økonomisk sikret, hvis virksomheden stopper",
        "Du er korrekt stillet i dagpengesystemet",
        "Du har kvalificeret sparring om drift",
        "Du kan træffe beslutninger på et oplyst grundlag",
        "Du minimerer juridisk risiko i din virksomhed",
        "Du har professionel hjælp ved kontrakter og tvivl",
        "Du sikrer din personlige indkomst",
        "Du reducerer økonomisk sikkerhed hvis virksomheden lukker",
        "Du har styr på både privatøkonomi og virksomhedens drift",
        "Du har overblik, hvis din situation ændrer sig",
        "Du er dækket både privat, juridisk og økonomisk",
        "Du er dækket, uanset hvad der sker",
        "Du er økonomisk sikret, hvis virksomheden stopper",
        "Du mindsker indkomsttab ved lukning",
        "Du kombinerer økonomisk sikkerhed med kvalificeret sparring",
        "Du har et økonomisk fallback, hvis virksomheden må lukke",
        "Du får professionel sparring og juridisk sikkerhed",
        "Du er økonomisk sikret og juridisk korrekt stillet",
        "Du reducerer risiko både privat og i virksomheden",
        "Du reducerer både økonomisk og juridisk risiko",
        "Du er dækket privat og godt rustet i din virksomhed"
    ]

    const combinationToAdvantages: { [key: string]: number[]} = {
        "unemploymentBenefits": [5],
        "union": [1, 2],
        "salaryInsurance": [3, 4],
        "package": [5, 6, 16],
        "unemploymentBenefits,union": [7, 8],
        "unemploymentBenefits,union,salaryInsurance": [5, 6, 16],
        "unemploymentBenefits,salaryInsurance": [11, 12],
        "union,salaryInsurance": [13, 14]
    }

    const combinationToAdvantagesSelfemployed: {[key: string]: number[]} = {
        "unemploymentBenefits": [0, 1],
        "basic": [2, 3],
        "hrJura": [4, 5],
        "salaryInsurance": [6, 7],
        "unemploymentBenefits,basic": [8, 9],
        "unemploymentBenefits,basic,hrJura,salaryInsurance": [10, 11],
        "unemploymentBenefits,salaryInsurance": [12, 13],
        "basic,salaryInsurance": [14, 15],
        "basic,hrJura": [16],
        "unemploymentBenefits,hrJura": [17, 18],
        "salaryInsurance,hrJura": [19],
        "unemploymentBenefits,hrJura,basic": [20]
    }
    
    function getAdvantagesForProducts(selectedProducts: string[]) {
        const products = selectedProducts.sort().join(",");
        if(selectedProducts.length === 0) {
                setChosenAdvantages([])
        } else {
            if(payload.occupation === "SelfEmployed") {
                if(selectedProducts.length === 1) {
                    const combinations = combinationToAdvantagesSelfemployed[products] || [];
                    setChosenAdvantages(combinations.map(i => advantagesSelfemployed[i]))   
                } else if(selectedProducts.length === 4) {
                    let combination = combinationToAdvantagesSelfemployed["unemploymentBenefits,basic,hrJura,salaryInsurance"]
                    setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                } else if (selectedProducts.length === 3) {
                    let combination = combinationToAdvantagesSelfemployed["unemploymentBenefits,hrJura,basic"]
                    setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                } else if(selectedProducts.length === 2 && selectedProducts.includes("unemploymentBenefits") && selectedProducts.includes("basic")) {
                    let combination = combinationToAdvantagesSelfemployed["unemploymentBenefits,basic"]
                    setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                } else {
                    if(!selectedProducts.includes("basic") && !selectedProducts.includes("hrJura")) {
                        let combination = combinationToAdvantagesSelfemployed["unemploymentBenefits,salaryInsurance"]
                        setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                    }
                    if(selectedProducts.includes("basic") && selectedProducts.includes("salaryInsurance")) {
                        let combination = combinationToAdvantagesSelfemployed["basic,salaryInsurance"]
                        setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                    }
                    if(selectedProducts.includes("basic") && selectedProducts.includes("hrJura")) {
                        let combination = combinationToAdvantagesSelfemployed["basic,hrJura"]
                        setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                    }
                    if(selectedProducts.includes("unemploymentBenefits")) {
                        let combination = combinationToAdvantagesSelfemployed["unemploymentBenefits,hrJura"]
                        setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                    }
                    if(selectedProducts.includes("salaryInsurance")) {
                        let combination = combinationToAdvantagesSelfemployed["salaryInsurance,hrJura"]
                        setChosenAdvantages(combination.map(i => advantagesSelfemployed[i]))
                    }
                }
            } else {
                if(selectedProducts.length === 1) {
                    const combinations = combinationToAdvantages[products] || [];
                    setChosenAdvantages(combinations.map(i => advantages[i]))    
                } else {
                    if(!selectedProducts.includes("unemploymentBenefits") && selectedProducts.includes("union")) {
                        let combination = combinationToAdvantages["union,salaryInsurance"]
                        setChosenAdvantages(combination.map(i => advantages[i]))
                    }
                    if(selectedProducts.includes("unemploymentBenefits") && selectedProducts.includes("union") && !selectedProducts.includes("salaryInsurance")) {
                        let combination = combinationToAdvantages["unemploymentBenefits,union"]
                        setChosenAdvantages(combination.map(i => advantages[i]))
                    }
                    if(selectedProducts.includes("unemploymentBenefits") && selectedProducts.includes("union") && selectedProducts.includes("salaryInsurance")) {
                        let combination = combinationToAdvantages["unemploymentBenefits,union,salaryInsurance"]
                        setChosenAdvantages(combination.map(i => advantages[i]))
                    }
                    if(selectedProducts.includes("unemploymentBenefits") && !selectedProducts.includes("union") && selectedProducts.includes("salaryInsurance")) {
                        let combination = combinationToAdvantages["unemploymentBenefits,salaryInsurance"]
                        setChosenAdvantages(combination.map(i => advantages[i]))
                    }
                }
            }
        }
    }

    useEffect(() => {
         const newProducts: string[] = [];

        if(payload.occupation === "SelfEmployed") {
            if(payload.wantsUnemploymentBenefits) {
                newProducts.push("unemploymentBenefits")
            }
            if(payload.wantsSalaryInsurance) {
                newProducts.push("salaryInsurance")
            }
            if(payload.wantsBasic) {
                newProducts.push("basic")
            }
            if(payload.wantsHRJura) {
                newProducts.push("hrJura")
            }

            getAdvantagesForProducts(newProducts)

        } else {
            if(payload.wantsUnemploymentBenefits && !payload.wantsSalaryInsuranceAndUnion) {
                newProducts.push("unemploymentBenefits")
            }
            if(payload.wantsUnion) {
                newProducts.push("union")
            }
            if(payload.wantsSalaryInsurance) {
                newProducts.push("salaryInsurance")
            }
            if(payload.wantsSalaryInsuranceAndUnion) {
                newProducts.push("package")
            }

            getAdvantagesForProducts(newProducts)
        }
    }, [payload.wantsUnemploymentBenefits, payload.wantsUnion, payload.wantsSalaryInsurance, payload.wantsSalaryInsuranceAndUnion, payload.wantsBasic, payload.wantsHRJura])

    
    useEffect(() => {
        setSelfemployedProducts([""])
        const products: string[] = []
        
        if(payload.wantsBasic) {
            products.push("Ase selvstændig")
        }
        if(payload.wantsHRJura) {
            products.push("HR Jura")
        }
        if(payload.occupation && payload.wantsSalaryInsurance) {
            products.push("lønsikring")
        }
        
        if(products.length > 1) {
            setShowSelfemployedProductsInfo(true)
            setSelfemployedProducts(products)
        } else {
            setShowSelfemployedProductsInfo(false)
        }
    }, [payload])

    const clickContact = () => {
        openHelpForm();
        Analytics.getInstance().trackContactLeanModalPopped()
    }

    //Check for salaryInsurance price
    const getSalaryInsurancePrice = useMemo(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const urlPrice = urlParams.get("salaryinsurance");

        setSalaryInsurancePrice(Number(urlPrice))

        if(urlPrice) {
            return Number(urlPrice) + " kr./md.";
        } else {
            "Vi kontakter dig"
        }
    }, []);

    return (
        <BasketWrapper marginTop={marginTop}>
            <BasketContent>
                <BasketHeader>
                    <BasketHeaderIcon src="/assets/images/basket-icon.png"/>
                    <BasketHeadline>Din oversigt</BasketHeadline>
                </BasketHeader>
                <BasketProgress>
                    <BasketProgressText>Trin {numberStep} ud af 6</BasketProgressText>
                    <BasketProgressbar>
                        <BasketProgressFill width={progressPercent}/>
                    </BasketProgressbar>
                </BasketProgress>
                {accordionRoute != AccordionRoutesEnum.OccupationStep && (
                <>
                <BasketMembershipWrapper>
                    <BasketProgressText>Medlemskab</BasketProgressText>
                    {chosenAdvantages.length === 0 && (
                        <BasketMembershipRow>
                            <BasketProduct>Ingen produkter valgt</BasketProduct>
                        </BasketMembershipRow>
                    )}
                    {payload.wantsUnemploymentBenefits && !payload.wantsSalaryInsuranceAndUnion && (
                    <BasketMembershipRow>
                        <div style={{display: "flex"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.unemploymentInsurance.title}</BasketProduct>
                        </div>
                        <div>
                            <BasketProductPrice>{unemploymentBenefitsPrice()} kr./md.</BasketProductPrice>
                        </div>
                    </BasketMembershipRow>
                    )}
                    {payload.wantsUnion && (
                    <>
                    <BasketMembershipRow>
                        <div style={{display: "flex"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.union.title}</BasketProduct>
                        </div>
                        <div>
                            <BasketProductPrice>{translations?.productsStep.union.price} kr./md.</BasketProductPrice>
                        </div>
                    </BasketMembershipRow>
                    {payload.wantsUnemploymentBenefits && payload.wantsUnion && payload.occupation !== "Student" && (
                        <BasketUnionOffer>Gratis i 6 måneder</BasketUnionOffer>
                    )}
                    </>
                    )}
                    {payload.wantsSalaryInsurance && (
                    <BasketMembershipRow>
                        <div style={{display: "flex"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.salaryGuarantee.title}</BasketProduct>
                        </div>
                        <div>
                            {!showSelfemployedProductInfo && (
                            <BasketProductPrice>{getSalaryInsurancePrice}</BasketProductPrice>
                            )}
                        </div>
                    </BasketMembershipRow>
                    )}
                    {payload.wantsSalaryInsuranceAndUnion && (
                    <BasketPackageRow>
                        <div style={{display: "flex", width: "60%"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.salaryInsuranceAndUnion.title}</BasketProduct>
                        </div>
                        <div>
                            <BasketProductPrice>{translations?.productsStep.salaryInsuranceAndUnion.price} kr./md.</BasketProductPrice>
                        </div>
                    </BasketPackageRow>
                    )}
                    {payload.wantsBasic && (
                    <BasketMembershipRow>
                        <div style={{display: "flex"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.basic.title}</BasketProduct>
                        </div>
                        <div>
                            {!showSelfemployedProductInfo && (
                            <BasketProductPrice>Vi kontakter dig</BasketProductPrice>
                            )}
                        </div>
                    </BasketMembershipRow>
                    )}
                    {payload.wantsHRJura && (
                    <BasketMembershipRow>
                        <div style={{display: "flex"}}>
                            <BasketCheckmark/>
                            <BasketProduct>{translations?.productsStep.hrjura.title}</BasketProduct>
                        </div>
                        <div>
                            {!showSelfemployedProductInfo && (
                            <BasketProductPrice>Vi kontakter dig</BasketProductPrice>
                            )}
                        </div>
                    </BasketMembershipRow>
                    )}
                </BasketMembershipWrapper>
                <BasketValueList>
                {chosenAdvantages.map((advantage, idx) => (
                    <BasketValueItem key={idx}>{advantage}</BasketValueItem>
                ))}
                </BasketValueList> 
                </>
                )}
            </BasketContent>
            {accordionRoute != AccordionRoutesEnum.OccupationStep && (
            <BasketTotalWrapper>
               {!payload.wantsSalaryInsuranceAndUnion && (
                <BasketTotalRow>
                    <BasketTotal>I alt</BasketTotal>
                    <BasketTotalPrice>{formatNumber(fullPrice)} kr./md.</BasketTotalPrice>
                </BasketTotalRow>
                )}
                {payload.wantsUnemploymentBenefits && payload.wantsUnion && payload.occupation !== "Student" && payload.occupation !== "SelfEmployed" && (
                <BasketTotalRow>
                    <BasketTotalText>Efter 6 mdr.</BasketTotalText>  
                    <BasketTotal>{basePrice} kr./md.</BasketTotal>
                </BasketTotalRow>
                
                )}
                {payload.occupation === "SelfEmployed" && showSelfemployedProductInfo && (
                    <BasketPackageInfoContainer>
                    <BasketPackageInfoHeader>
                        <BasketPackageInfoIcon src="/assets/images/info-circle.png"/>
                        <BasketPackageInfoHeadline>Vi kontakter dig om</BasketPackageInfoHeadline>
                    </BasketPackageInfoHeader>
                    <BasketPackageInfoUl>
                        {selfemployedProducts.map((product, idx) => (
                            <BasketPackageInfoLi key={idx}>{product}</BasketPackageInfoLi>
                        ))}
                    </BasketPackageInfoUl>
                </BasketPackageInfoContainer>
                )}
                {payload.wantsSalaryInsuranceAndUnion && (
                <>
                <BasketTotalRow>
                    <BasketTotal>I alt</BasketTotal>
                    <BasketTotalPrice>{translations?.productsStep.salaryInsuranceAndUnion.price} kr./md.</BasketTotalPrice>
                </BasketTotalRow>
                <BasketPackageInfoContainer>
                    <BasketPackageInfoHeader>
                        <BasketPackageInfoIcon src="/assets/images/info-circle.png"/>
                        <BasketPackageInfoHeadline>Sådan foregår betalingen:</BasketPackageInfoHeadline>
                    </BasketPackageInfoHeader>
                    <BasketPackageInfoUl>
                        <BasketPackageInfoLi>Vi opretter dig i a-kassen.</BasketPackageInfoLi>
                        <BasketPackageInfoLi>Vi kontakter dig om fagforening og lønsikring, så du får den bedste dækning. Når du har tegnet din lønsikring, får du en opkrævning på {packagePrice} kr.</BasketPackageInfoLi>
                    </BasketPackageInfoUl>
                </BasketPackageInfoContainer>
                </>
                )}
                {/* When salaryinsurance is bought from salaryinsuranceConfigCard */}
                 {payload.wantsSalaryInsurance && salaryInsurancePrice && salaryInsurancePrice > 0 && (
                <BasketPackageInfoContainer>
                    <BasketPackageInfoHeader>
                        <BasketPackageInfoIcon src="/assets/images/info-circle.png"/>
                        <BasketPackageInfoHeadline>Sådan foregår betalingen:</BasketPackageInfoHeadline>
                    </BasketPackageInfoHeader>
                    <BasketPackageInfoUl>
                        <BasketPackageInfoLi>Vi opretter dig i a-kassen.</BasketPackageInfoLi>
                        <BasketPackageInfoLi>Du modtager en betalingsanmodning i MobilePay på {translations?.productsStep.unemploymentInsurance.price} kr. for a-kassen.</BasketPackageInfoLi>
                        <BasketPackageInfoLi>Vi kontakter dig om din lønsikring, for at sikre at du får den rette løsning.</BasketPackageInfoLi>
                    </BasketPackageInfoUl>
                </BasketPackageInfoContainer>
                )}
                {accordionRoute == AccordionRoutesEnum.PaymentStep && IsDesktop && (
                <InfoText>Der er 14 dages fortrydelsesret på din indmeldelse</InfoText>    
                )}
            </BasketTotalWrapper>
            )}
            <Contact onClick={() => clickContact()}>Snak med en rådgiver</Contact>
        </BasketWrapper>
        
    )
} 